import React, { createContext, useContext, useEffect, useState } from 'react';

import de from '../locales/de.json';
import en from '../locales/en.json';
import es from '../locales/es.json';
import fr from '../locales/fr.json';
import it from '../locales/it.json';
import pl from '../locales/pl.json';
import sv from '../locales/sv.json';

export interface I18nContextType {
  language: string;
  setLanguage: (language: string) => void;
  t: (key: string) => string;
  locales: Record<string, any>;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

const locales = {
  en,
  de,
  fr,
  es,
  it,
  pl,
  sv,
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguage] = useState<string>(() => {
    // Try to get language from localStorage
    const savedLanguage = localStorage.getItem('ce-marking-guide-language');
    if (savedLanguage && locales[savedLanguage as keyof typeof locales]) {
      return savedLanguage;
    }

    // Try to get language from browser
    const browserLanguage = navigator.language.split('-')[0];
    if (locales[browserLanguage as keyof typeof locales]) {
      return browserLanguage;
    }

    // Default to English
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('ce-marking-guide-language', language);
  }, [language]);

  const t = (key: string): string => {
    const keys = key.split('.');
    let value: any = locales[language as keyof typeof locales] || locales.en;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Fallback to English if key not found
        value = locales.en;
        for (const fallbackKey of keys) {
          if (value && typeof value === 'object' && fallbackKey in value) {
            value = value[fallbackKey];
          } else {
            return key; // Return the key if translation not found
          }
        }
        break;
      }
    }

    return typeof value === 'string' ? value : key;
  };

  const value: I18nContextType = {
    language,
    setLanguage,
    t,
    locales,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = (): I18nContextType => {
  const context = useContext(I18nContext);
  if (context === undefined) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return context;
};
